import { useState } from 'react';

import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { ScrollList } from '@/components/ui/ScrollList';
import { ErrorState, LoadingState } from '@/components/ui/StateMessage';
import { useToast } from '@/components/ui/Toast';
import { PERMISSIONS } from '@/constants/permissions';
import { usePermissions } from '@/features/auth';
import { formatDate } from '@/utils/date';
import { getErrorMessage } from '@/utils/errors';
import { formatMoney } from '@/utils/money';
import { fullName } from '@/utils/text';

import { useDownloadInvoicePdf, useInvoice, useWhatsappInvoiceLink } from '../../hooks/useInvoices';
import {
  type InvoiceDetail,
  PAYMENT_METHOD_LABEL,
  type PaymentKind,
} from '../../schemas/invoice.schema';
import { InvoicePaymentBadge } from '../InvoicePaymentBadge';
import { RecordPaymentDialog } from '../RecordPaymentDialog';
import styles from './InvoiceDialog.module.css';

export interface InvoiceDialogProps {
  /** The bill to show; null closes the dialog. */
  invoiceId: number | null;
  onClose: () => void;
}

/** One bill: what was sold, the amounts, its payments, and its PDF. */
export function InvoiceDialog({ invoiceId, onClose }: InvoiceDialogProps) {
  const invoice = useInvoice(invoiceId);

  return (
    <Dialog
      open={invoiceId !== null}
      onClose={onClose}
      size="lg"
      title={invoice.data ? `Bill ${invoice.data.invoiceNumber}` : 'Bill'}
      description={invoice.data?.description}
      footer={
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
      }
    >
      {invoice.isPending && <LoadingState />}
      {invoice.isError && (
        <ErrorState
          error={invoice.error}
          onRetry={() => {
            void invoice.refetch();
          }}
        />
      )}
      {invoice.data && <InvoiceBody invoice={invoice.data} />}
    </Dialog>
  );
}

/** Payments drawn at a time in the dialog's scrolling list. */
const PAYMENTS_STEP = 10;

function InvoiceBody({ invoice }: { invoice: InvoiceDetail }) {
  const { can } = usePermissions();
  const toast = useToast();
  const [recording, setRecording] = useState<PaymentKind | null>(null);
  const [shownPayments, setShownPayments] = useState(PAYMENTS_STEP);
  const showMorePayments = () => {
    setShownPayments((shown) => shown + PAYMENTS_STEP);
  };
  const download = useDownloadInvoicePdf();
  const whatsapp = useWhatsappInvoiceLink();

  const canPay = can(PERMISSIONS.PAYMENTS_WRITE);
  const settled = invoice.paymentState === 'paid';
  const cancelled = invoice.paymentState === 'cancelled';
  // Nothing is due on a paid bill; a cancelled one only ever takes money back out.
  const canTakeMoney = canPay && !settled && !cancelled;
  const canRefund = canPay && invoice.amountPaid !== '0.00';

  return (
    <div className={styles.body}>
      <div className={styles.header}>
        <div className={styles.headerText}>
          <p className={styles.member}>
            {fullName(invoice.member.firstName, invoice.member.lastName)}
          </p>
          <p className={styles.muted}>
            {invoice.member.memberCode} · {invoice.member.mobile} · Issued{' '}
            {formatDate(invoice.issueDate)}
          </p>
        </div>
        <InvoicePaymentBadge state={invoice.paymentState} />
      </div>

      <div className={styles.columns}>
        <section className={styles.panel} aria-label="Amounts">
          <h3 className={styles.panelTitle}>Amount</h3>
          <dl className={styles.amounts}>
            <div className={styles.row}>
              <dt>{invoice.description}</dt>
              <dd className="tabular">{formatMoney(invoice.amount)}</dd>
            </div>
            {invoice.discount !== '0.00' && (
              <div className={styles.row}>
                <dt>Discount</dt>
                <dd className="tabular">−{formatMoney(invoice.discount)}</dd>
              </div>
            )}
            <div className={`${styles.row} ${styles.total}`}>
              <dt>Total</dt>
              <dd className="tabular">{formatMoney(invoice.total)}</dd>
            </div>
            <div className={styles.row}>
              <dt>Paid so far</dt>
              <dd className="tabular">{formatMoney(invoice.amountPaid)}</dd>
            </div>
          </dl>
          {/* Nothing is owed on a cancelled bill, whatever `total − amountPaid` says. */}
          <div
            className={`${styles.due} ${cancelled ? styles.dueVoid : settled ? styles.dueSettled : styles.dueOwed}`}
          >
            <span>{cancelled ? 'Cancelled' : settled ? 'Settled' : 'Still due'}</span>
            <strong className="tabular">{cancelled ? '—' : formatMoney(invoice.dueAmount)}</strong>
          </div>
        </section>

        <section className={styles.panel} aria-label="Payments">
          <h3 className={styles.panelTitle}>Payments</h3>
          {invoice.payments.length === 0 ? (
            <p className={styles.empty}>Nothing recorded against this bill yet.</p>
          ) : (
            // The API sends every payment; they are drawn a few at a time as the list scrolls.
            <ScrollList
              hasMore={shownPayments < invoice.payments.length}
              onLoadMore={showMorePayments}
            >
              <ul className={styles.payments}>
                {invoice.payments.slice(0, shownPayments).map((payment) => (
                  <li key={payment.id} className={styles.payment}>
                    <span className={styles.paymentText}>
                      <span className={styles.paymentWhat}>
                        {payment.kind === 'refund' ? 'Refund' : 'Payment'} ·{' '}
                        {PAYMENT_METHOD_LABEL[payment.method]}
                      </span>
                      <span className={styles.muted}>
                        {formatDate(payment.paidOn)}
                        {payment.reference ? ` · ${payment.reference}` : ''}
                      </span>
                    </span>
                    <span
                      className={`tabular ${payment.kind === 'refund' ? styles.refundAmount : styles.paymentAmount}`}
                    >
                      {payment.kind === 'refund' ? '−' : ''}
                      {formatMoney(payment.amount)}
                    </span>
                  </li>
                ))}
              </ul>
            </ScrollList>
          )}
        </section>
      </div>

      <div className={styles.actions}>
        <div className={styles.group}>
          {canTakeMoney && (
            <Button
              onClick={() => {
                setRecording('payment');
              }}
            >
              Record payment
            </Button>
          )}
          {canRefund && (
            <Button
              variant="secondary"
              onClick={() => {
                setRecording('refund');
              }}
            >
              Record refund
            </Button>
          )}
        </div>
        <div className={styles.group}>
          <Button
            variant="secondary"
            icon="printer"
            isLoading={download.isPending}
            onClick={() => {
              download.mutate(
                { id: invoice.id, fallbackName: `${invoice.invoiceNumber}.pdf` },
                {
                  onError: (error) => {
                    toast.error(getErrorMessage(error));
                  },
                },
              );
            }}
          >
            Download PDF
          </Button>
          {/* A cancelled bill is not something to send a member. */}
          {!cancelled && (
            <Button
              variant="secondary"
              icon="whatsapp"
              isLoading={whatsapp.isPending}
              onClick={() => {
                // Opened now, inside the tap, so a pop-up blocker lets it through; it is pointed
                // at WhatsApp once the link exists.
                const chat = window.open('', '_blank');
                if (chat) chat.opener = null;
                whatsapp.mutate(invoice, {
                  onSuccess: (url) => {
                    if (chat) chat.location.href = url;
                    else window.open(url, '_blank');
                  },
                  onError: (error) => {
                    chat?.close();
                    toast.error(getErrorMessage(error));
                  },
                });
              }}
            >
              WhatsApp
            </Button>
          )}
        </div>
      </div>

      {recording !== null && (
        <RecordPaymentDialog
          open
          kind={recording}
          invoice={invoice}
          onClose={() => {
            setRecording(null);
          }}
        />
      )}
    </div>
  );
}
